/* ============================================================================
   CRANK BUILDER · <crank-builder> custom element
   ----------------------------------------------------------------------------
   Guided crankset configurator over real RACEIT products.

     · assets/crank-builder-catalog.js — window.CBCatalog, generated by
       build-crank-builder-catalog.cjs (compatibility derived automatically
       from the live feed, refined by metafields and the reviewed overrides).
     · assets/crank-builder-rules.js  — window.CBRules, the compatibility and
       completeness engine. Pure logic, unit-tested by assert-crank-compat.cjs.
     · assets/crank-builder.css       — all styles, scoped under #crank-builder.

   This file is presentation only: it renders steps, reflects state and posts
   the finished build to the cart. Every "can I pick this?" question goes to
   CBRules, so the storefront and the assertions share one source of truth.

   CART
     One /cart/add.js call with an items array — arms, spider, chainrings and
     bottom bracket each stay a real line with their own price and stock, tied
     together by a CK-… build id. Same approach as the Bike Builder; no bundle
     product and no discount to keep in sync.
   ========================================================================== */
(function () {
  'use strict';

  class CrankBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cbInit) return;
      var self = this, tries = 0;
      (function boot() {
        if (window.CBCatalog && window.CBRules) { self.init(); return; }
        if (++tries > 120) return; // ~2s; both scripts are defer'd before us
        requestAnimationFrame(boot);
      })();
    }

    init() {
      var root = this;
      if (root.dataset.cbInit || !window.CBCatalog || !window.CBRules) return;
      root.dataset.cbInit = '1';

      var C = window.CBCatalog;
      var R = window.CBRules.create(C);
      var P = R.parts;

      /* ============================ i18n ============================ */
      var LANG = (document.documentElement.lang || 'es').slice(0, 2).toLowerCase();
      var I18N = {
        es: {
          steps: ['Eje', 'Bielas', 'Araña / Direct Mount', 'Platos', 'Pedalier', 'Resumen'],
          spindleTitle: 'Estándar de eje',
          spindleHelp: 'El diámetro del eje de las bielas. Es lo que decide todo lo demás.',
          shellTitle: 'Estándar de pedalier de tu cuadro',
          shellHelp: 'Solo para afinar el pedalier. Comprueba el estándar exacto en la ficha de tu bici.',
          shellAny: 'No lo sé',
          speedsTitle: 'Velocidades de la transmisión',
          speedsHelp: 'Elige el número de velocidades de tu transmisión.',
          crankTitle: 'Elige las bielas',
          crankHelp: 'Solo bielas, sin conjunto completo: elige o confirma la araña y después los platos.',
          spiderTitle: 'Araña, potenciómetro o plato Direct Mount',
          spiderHelp: 'Elige la interfaz que encaja en tus bielas. Un plato Direct Mount sustituye la araña, los platos separados y su tornillería.',
          ringsTitle: 'Platos',
          ringsHelp: 'Platos de carretera 110 BCD × 4 que atornillan a tu araña.',
          hardwareTitle: 'Tornillería 2x',
          hardwareHelp: 'Necesitas tornillos 2x compatibles para el montaje. Añade el kit o confirma que reutilizarás los tuyos.',
          ownBolts: 'Ya tengo tornillos 2x compatibles',
          ownBoltsHelp: 'No añadiremos un kit al carrito.',
          bbTitle: 'Caja de pedalier (opcional)',
          bbHelp: 'Opcional: no hace falta para terminar el montaje. Filtramos por tu eje y, si nos lo dices, por el estándar de pedalier de tu cuadro.',
          noneBb: 'Sin pedalier',
          summaryTitle: 'Tu crankset',
          flatView: 'Vista orientativa del montaje desde el lado de los platos.',
          flatBb: 'El pedalier seleccionado se monta detrás del conjunto.',
          flatLoading: 'Preparando la imagen de tu montaje…',
          flatError: 'La imagen del montaje no está disponible en este momento.',
          included: 'Incluido con las bielas',
          includedSpider: 'Estas bielas ya llevan araña',
          includedRings: 'Estas bielas ya llevan platos',
          directMountIncluded: 'Integrado en el plato Direct Mount',
          notRequired: 'No necesaria con Direct Mount',
          none: 'Ninguno',
          nothingHere: 'Nada compatible en este paso con lo que llevas elegido.',
          next: 'Continuar', back: 'Atrás', edit: 'Editar',
          addBuild: 'Añadir crankset al carrito', addShort: 'Al carrito',
          adding: 'Añadiendo…', added: '¡Añadido! Abriendo el carrito…',
          cartErr: 'No se ha podido añadir. Inténtalo de nuevo.',
          reset: 'Empezar de nuevo', copy: 'Copiar resumen', copied: '¡Copiado!',
          total: 'Total', weight: 'Peso orientativo', est: 'estimado',
          weightNote: 'Peso orientativo según datos publicados y estimaciones documentadas. Puede variar por longitud, desarrollo, herrajes y tolerancias de fabricación. Si reutilizas tus tornillos compatibles, el cálculo excluye los 9,5 g del kit CYBREI.',
          weightUnavailable: 'Peso no verificado para esta configuración',
          ready: 'Montaje completo: las interfaces y medidas seleccionadas son compatibles.',
          progress: 'Paso {current} de {total}',
          pricesLive: 'Precios de tienda actualizados.', pricesCatalog: 'Precios de catálogo: ',
          parts: 'Componentes', spec: 'Ficha', from: 'desde',
          blocked: 'Para añadir al carrito te falta:',
          slotArms: 'las bielas', slotSpindle: 'el eje', slotBb: 'el pedalier',
          slotSpider: 'la araña', slotRings: 'los platos', slotBolts: 'la tornillería 2x',
          specMm: 'Longitud', specSpindle: 'Eje', specSpeeds: 'Velocidades', specShell: 'Cuadro', specBcd: 'Anclaje', specRing: 'Desarrollo', specPm: 'Potenciómetro',
          yes: 'Sí', no: 'No',
          why: {
            needSpindle: 'Elige antes el estándar de eje',
            needCrank: 'Elige antes las bielas',
            needSpider: 'Elige antes la araña',
            mount: 'Montaje distinto al de tus bielas',
            bcd: 'Anclaje distinto: tu araña es {need}',
            family: 'Solo compatible con {got}',
            unknown: 'No podemos confirmar que encaje',
            spindle: 'No existe con eje {need}',
            shell: 'No existe para el ancho {need}',
            speeds: 'No es para {need} velocidades',
            drivetrain: 'No está confirmado para {need}',
            layout: 'Tornillería incompatible con estos platos',
            included: 'Ya incluido con las bielas',
            noRings: 'No tenemos platos que encajen',
          },
          propBuild: 'Crankset', propId: '_Crankset ID', propSpec: 'Configuración',
          ariaSummary: 'Resumen de tu montaje', ariaProgress: 'Progreso del configurador', ariaSteps: 'Pasos',
          spindleStraight24: '24 mm recto', spindleHollowtech: 'Tipo Hollowtech II · 24 mm recto',
          shellItalian: 'Italiano', mountSram8: 'SRAM 8 pernos', mountSram3: 'SRAM 3 pernos',
        },
        en: {
          steps: ['Axle', 'Crank arms', 'Spider / Direct Mount', 'Chainrings', 'Bottom bracket', 'Summary'],
          spindleTitle: 'Axle standard',
          spindleHelp: 'The crank spindle diameter. It decides everything that follows.',
          shellTitle: 'Your frame’s bottom-bracket standard',
          shellHelp: 'Only to narrow the bottom bracket. Check the exact standard in your bike’s spec sheet.',
          shellAny: 'I don’t know',
          speedsTitle: 'Drivetrain speeds',
          speedsHelp: 'Choose the number of speeds in your drivetrain.',
          crankTitle: 'Choose your crank arms',
          crankHelp: 'Crank arms only, not complete cranksets: choose or confirm the spider, then the chainrings.',
          spiderTitle: 'Spider, power meter or Direct Mount ring',
          spiderHelp: 'Choose the interface that fits your arms. A Direct Mount ring replaces the spider, separate chainrings and their bolts.',
          ringsTitle: 'Chainrings',
          ringsHelp: 'Road 110 BCD × 4 chainrings that bolt to your spider.',
          hardwareTitle: '2x mounting hardware',
          hardwareHelp: 'Compatible 2x bolts are needed for assembly. Add the kit or confirm you will reuse your own.',
          ownBolts: 'I already have compatible 2x bolts',
          ownBoltsHelp: 'No bolt kit will be added to the cart.',
          bbTitle: 'Bottom bracket (optional)',
          bbHelp: 'Optional — your build is complete without it. Filtered by your axle and, if you tell us, by your frame’s bottom-bracket standard.',
          noneBb: 'No bottom bracket',
          summaryTitle: 'Your crankset',
          flatView: 'Illustrative assembly view from the chainring side.',
          flatBb: 'The selected bottom bracket fits behind the assembly.',
          flatLoading: 'Preparing your assembly image…',
          flatError: 'The assembly image is currently unavailable.',
          included: 'Included with the arms',
          includedSpider: 'These arms already come with a spider',
          includedRings: 'These arms already come with chainrings',
          directMountIncluded: 'Integrated in the Direct Mount ring',
          notRequired: 'Not required with Direct Mount',
          none: 'None',
          nothingHere: 'Nothing here is compatible with what you have chosen.',
          next: 'Continue', back: 'Back', edit: 'Edit',
          addBuild: 'Add crankset to cart', addShort: 'Add to cart',
          adding: 'Adding…', added: 'Added! Opening your cart…',
          cartErr: 'Could not add. Please try again.',
          reset: 'Start over', copy: 'Copy summary', copied: 'Copied!',
          total: 'Total', weight: 'Indicative weight', est: 'estimated',
          weightNote: 'Indicative weight based on published data and documented estimates. It can vary with crank length, gearing, hardware and manufacturing tolerances. If you reuse compatible bolts, the calculation excludes the CYBREI kit’s 9.5 g.',
          weightUnavailable: 'Weight not verified for this configuration',
          ready: 'Complete build: the selected interfaces and dimensions are compatible.',
          progress: 'Step {current} of {total}',
          pricesLive: 'Store prices refreshed.', pricesCatalog: 'Catalog prices: ',
          parts: 'Components', spec: 'Spec', from: 'from',
          blocked: 'Still needed before you can add to cart:',
          slotArms: 'crank arms', slotSpindle: 'a spindle', slotBb: 'a bottom bracket',
          slotSpider: 'a spider', slotRings: 'chainrings', slotBolts: '2x mounting hardware',
          specMm: 'Length', specSpindle: 'Spindle', specSpeeds: 'Speeds', specShell: 'Frame', specBcd: 'Bolt circle', specRing: 'Gearing', specPm: 'Power meter',
          yes: 'Yes', no: 'No',
          why: {
            needSpindle: 'Choose your axle standard first',
            needCrank: 'Choose your crank arms first',
            needSpider: 'Choose a spider first',
            mount: 'Different mount from your arms',
            bcd: 'Different bolt circle: yours is {need}',
            family: 'Only fits {got}',
            unknown: 'We cannot confirm this fits',
            spindle: 'Not made with a {need} axle',
            shell: 'Not made for the {need} shell',
            speeds: 'Not for {need} speed',
            drivetrain: 'Not confirmed for {need}',
            layout: 'Mounting hardware does not fit these chainrings',
            included: 'Already included with the arms',
            noRings: 'We have no chainrings that fit it',
          },
          propBuild: 'Crankset', propId: '_Crankset ID', propSpec: 'Setup',
          ariaSummary: 'Build summary', ariaProgress: 'Configurator progress', ariaSteps: 'Steps',
          spindleStraight24: '24 mm straight', spindleHollowtech: 'Hollowtech II type · 24 mm straight',
          shellItalian: 'Italian', mountSram8: 'SRAM 8-bolt', mountSram3: 'SRAM 3-bolt',
        },
        de: {
          steps: ['Achse', 'Kurbeln', 'Spider / Direct Mount', 'Kettenblätter', 'Innenlager', 'Zusammenfassung'],
          spindleTitle: 'Achsstandard',
          spindleHelp: 'Der Durchmesser der Kurbelachse. Er bestimmt alles Weitere.',
          shellTitle: 'Innenlager-Standard deines Rahmens',
          shellHelp: 'Nur zur Eingrenzung des Innenlagers. Prüfe den genauen Standard im Datenblatt deines Rads.',
          shellAny: 'Weiß ich nicht',
          speedsTitle: 'Anzahl der Gänge',
          speedsHelp: 'Wähle die Anzahl der Gänge deines Antriebs.',
          crankTitle: 'Wähle deine Kurbeln',
          crankHelp: 'Nur Kurbelarme, kein Komplettset: Wähle oder bestätige zuerst den Spider und danach die Kettenblätter.',
          spiderTitle: 'Spider, Leistungsmesser oder Direct-Mount-Kettenblatt',
          spiderHelp: 'Wähle die Schnittstelle für deine Kurbeln. Ein Direct-Mount-Kettenblatt ersetzt Spider, separate Kettenblätter und Schrauben.',
          ringsTitle: 'Kettenblätter',
          ringsHelp: 'Rennrad-Kettenblätter 110 BCD × 4, die an deinen Spider geschraubt werden.',
          hardwareTitle: '2-fach-Montageschrauben',
          hardwareHelp: 'Für die Montage werden passende 2-fach-Schrauben benötigt. Füge das Set hinzu oder bestätige, dass du deine eigenen verwendest.',
          ownBolts: 'Ich habe bereits passende 2-fach-Schrauben',
          ownBoltsHelp: 'Es wird kein Schrauben-Set in den Warenkorb gelegt.',
          bbTitle: 'Innenlager (optional)',
          bbHelp: 'Optional — dein Aufbau ist auch ohne komplett. Gefiltert nach deiner Achse und, falls angegeben, nach dem Innenlager-Standard deines Rahmens.',
          noneBb: 'Kein Innenlager',
          summaryTitle: 'Dein Crankset',
          flatView: 'Illustrative Montageansicht von der Kettenblattseite.',
          flatBb: 'Das gewählte Tretlager sitzt hinter der Baugruppe.',
          flatLoading: 'Deine Montageansicht wird vorbereitet…',
          flatError: 'Die Montageansicht ist momentan nicht verfügbar.',
          included: 'Im Kurbelsatz enthalten',
          includedSpider: 'Diese Kurbeln haben bereits einen Spider',
          includedRings: 'Diese Kurbeln haben bereits Kettenblätter',
          directMountIncluded: 'Im Direct-Mount-Kettenblatt integriert',
          notRequired: 'Bei Direct Mount nicht erforderlich',
          none: 'Keine',
          nothingHere: 'Hier gibt es nichts, das zu deiner bisherigen Auswahl passt.',
          next: 'Weiter', back: 'Zurück', edit: 'Bearbeiten',
          addBuild: 'Crankset in den Warenkorb legen', addShort: 'In den Warenkorb',
          adding: 'Wird hinzugefügt…', added: 'Hinzugefügt! Warenkorb wird geöffnet…',
          cartErr: 'Hinzufügen fehlgeschlagen. Bitte versuche es erneut.',
          reset: 'Neu starten', copy: 'Zusammenfassung kopieren', copied: 'Kopiert!',
          total: 'Gesamt', weight: 'Ungefähres Gewicht', est: 'geschätzt',
          weightNote: 'Ungefähres Gewicht basierend auf veröffentlichten Daten und dokumentierten Schätzungen. Es kann je nach Kurbellänge, Übersetzung, Schrauben und Fertigungstoleranzen variieren. Wenn du deine eigenen passenden Schrauben verwendest, werden die 9,5 g des CYBREI-Sets bei der Berechnung nicht mitgezählt.',
          weightUnavailable: 'Gewicht für diese Konfiguration nicht verifiziert',
          ready: 'Aufbau vollständig: Die gewählten Schnittstellen und Maße sind kompatibel.',
          progress: 'Schritt {current} von {total}',
          pricesLive: 'Shoppreise aktualisiert.', pricesCatalog: 'Katalogpreise: ',
          parts: 'Komponenten', spec: 'Daten', from: 'ab',
          blocked: 'Für den Warenkorb fehlt noch:',
          slotArms: 'die Kurbeln', slotSpindle: 'die Achse', slotBb: 'das Innenlager',
          slotSpider: 'der Spider', slotRings: 'die Kettenblätter', slotBolts: 'die 2-fach-Schrauben',
          specMm: 'Länge', specSpindle: 'Achse', specSpeeds: 'Gänge', specShell: 'Rahmen', specBcd: 'Lochkreis', specRing: 'Übersetzung', specPm: 'Leistungsmesser',
          yes: 'Ja', no: 'Nein',
          why: {
            needSpindle: 'Wähle zuerst deinen Achsstandard',
            needCrank: 'Wähle zuerst deine Kurbeln',
            needSpider: 'Wähle zuerst einen Spider',
            mount: 'Anderer Anschluss als deine Kurbeln',
            bcd: 'Anderer Lochkreis: deiner ist {need}',
            family: 'Passt nur zu {got}',
            unknown: 'Wir können die Passform nicht bestätigen',
            spindle: 'Nicht mit {need}-Achse erhältlich',
            shell: 'Nicht für {need}-Innenlager erhältlich',
            speeds: 'Nicht für {need}-fach erhältlich',
            drivetrain: 'Nicht bestätigt für {need}',
            layout: 'Schrauben passen nicht zu diesen Kettenblättern',
            included: 'Bereits im Kurbelsatz enthalten',
            noRings: 'Wir haben keine passenden Kettenblätter',
          },
          propBuild: 'Crankset', propId: '_Crankset ID', propSpec: 'Konfiguration',
          ariaSummary: 'Zusammenfassung deines Aufbaus', ariaProgress: 'Fortschritt des Konfigurators', ariaSteps: 'Schritte',
          spindleStraight24: '24 mm gerade', spindleHollowtech: 'Hollowtech-II-Typ · 24 mm gerade',
          shellItalian: 'Italienisch', mountSram8: 'SRAM 8-Loch', mountSram3: 'SRAM 3-Loch',
        },
        fr: {
          steps: ['Axe', 'Manivelles', 'Araignée / Direct Mount', 'Plateaux', 'Boîtier de pédalier', 'Récapitulatif'],
          spindleTitle: 'Standard d’axe',
          spindleHelp: 'Le diamètre de l’axe de pédalier. C’est lui qui détermine tout le reste.',
          shellTitle: 'Standard de boîtier de pédalier de ton cadre',
          shellHelp: 'Uniquement pour affiner le boîtier de pédalier. Vérifie le standard exact sur la fiche technique de ton vélo.',
          shellAny: 'Je ne sais pas',
          speedsTitle: 'Nombre de vitesses de la transmission',
          speedsHelp: 'Choisis le nombre de vitesses de ta transmission.',
          crankTitle: 'Choisis tes manivelles',
          crankHelp: 'Manivelles seules, sans kit complet : choisis ou confirme l’araignée, puis les plateaux.',
          spiderTitle: 'Araignée, capteur de puissance ou plateau Direct Mount',
          spiderHelp: 'Choisis l’interface adaptée à tes manivelles. Un plateau Direct Mount remplace l’araignée, les plateaux séparés et leurs vis.',
          ringsTitle: 'Plateaux',
          ringsHelp: 'Plateaux route 110 BCD × 4 qui se boulonnent sur ton araignée.',
          hardwareTitle: 'Boulonnerie 2 plateaux',
          hardwareHelp: 'Des vis 2 plateaux compatibles sont nécessaires pour le montage. Ajoute le kit ou confirme que tu réutiliseras les tiennes.',
          ownBolts: 'J’ai déjà des vis 2 plateaux compatibles',
          ownBoltsHelp: 'Aucun kit de vis ne sera ajouté au panier.',
          bbTitle: 'Boîtier de pédalier (optionnel)',
          bbHelp: 'Optionnel — ton montage est complet sans lui. Filtré selon ton axe et, si tu le précises, selon le standard de boîtier de pédalier de ton cadre.',
          noneBb: 'Sans boîtier de pédalier',
          summaryTitle: 'Ton crankset',
          flatView: 'Vue indicative du montage côté plateaux.',
          flatBb: 'Le boîtier de pédalier sélectionné se monte derrière l’ensemble.',
          flatLoading: 'Préparation de l’image de votre montage…',
          flatError: 'L’image du montage est momentanément indisponible.',
          included: 'Inclus avec les manivelles',
          includedSpider: 'Ces manivelles ont déjà une araignée',
          includedRings: 'Ces manivelles ont déjà des plateaux',
          directMountIncluded: 'Intégré au plateau Direct Mount',
          notRequired: 'Inutile avec Direct Mount',
          none: 'Aucun',
          nothingHere: 'Rien de compatible à cette étape avec ce que tu as déjà choisi.',
          next: 'Continuer', back: 'Retour', edit: 'Modifier',
          addBuild: 'Ajouter le crankset au panier', addShort: 'Ajouter au panier',
          adding: 'Ajout en cours…', added: 'Ajouté ! Ouverture du panier…',
          cartErr: 'Impossible d’ajouter. Réessaie.',
          reset: 'Recommencer', copy: 'Copier le récapitulatif', copied: 'Copié !',
          total: 'Total', weight: 'Poids indicatif', est: 'estimé',
          weightNote: 'Poids indicatif basé sur les données publiées et des estimations documentées. Il peut varier selon la longueur des manivelles, le développement, la boulonnerie et les tolérances de fabrication. Si tu réutilises tes propres vis compatibles, le calcul exclut les 9,5 g du kit CYBREI.',
          weightUnavailable: 'Poids non vérifié pour cette configuration',
          ready: 'Montage complet : les interfaces et dimensions sélectionnées sont compatibles.',
          progress: 'Étape {current} sur {total}',
          pricesLive: 'Prix de la boutique actualisés.', pricesCatalog: 'Prix catalogue : ',
          parts: 'Composants', spec: 'Fiche', from: 'à partir de',
          blocked: 'Pour ajouter au panier, il te manque :',
          slotArms: 'les manivelles', slotSpindle: 'l’axe', slotBb: 'le boîtier de pédalier',
          slotSpider: 'l’araignée', slotRings: 'les plateaux', slotBolts: 'la boulonnerie 2 plateaux',
          specMm: 'Longueur', specSpindle: 'Axe', specSpeeds: 'Vitesses', specShell: 'Cadre', specBcd: 'Entraxe', specRing: 'Développement', specPm: 'Capteur de puissance',
          yes: 'Oui', no: 'Non',
          why: {
            needSpindle: 'Choisis d’abord ton standard d’axe',
            needCrank: 'Choisis d’abord tes manivelles',
            needSpider: 'Choisis d’abord une araignée',
            mount: 'Fixation différente de celle de tes manivelles',
            bcd: 'Entraxe différent : le tien est {need}',
            family: 'Compatible uniquement avec {got}',
            unknown: 'Impossible de confirmer la compatibilité',
            spindle: 'N’existe pas avec un axe {need}',
            shell: 'N’existe pas pour un boîtier {need}',
            speeds: 'Non prévu pour {need} vitesses',
            drivetrain: 'Non confirmé pour {need}',
            layout: 'Boulonnerie incompatible avec ces plateaux',
            included: 'Déjà inclus avec les manivelles',
            noRings: 'Nous n’avons pas de plateaux compatibles',
          },
          propBuild: 'Crankset', propId: '_Crankset ID', propSpec: 'Configuration',
          ariaSummary: 'Récapitulatif de ton montage', ariaProgress: 'Progression du configurateur', ariaSteps: 'Étapes',
          spindleStraight24: '24 mm droit', spindleHollowtech: 'Type Hollowtech II · 24 mm droit',
          shellItalian: 'Italien', mountSram8: 'SRAM 8 branches', mountSram3: 'SRAM 3 branches',
        },
        it: {
          steps: ['Asse', 'Guarniture', 'Spider / Direct Mount', 'Corone', 'Movimento centrale', 'Riepilogo'],
          spindleTitle: 'Standard dell’asse',
          spindleHelp: 'Il diametro dell’asse delle guarniture. È ciò che determina tutto il resto.',
          shellTitle: 'Standard del movimento centrale del tuo telaio',
          shellHelp: 'Solo per affinare il movimento centrale. Verifica lo standard esatto nella scheda tecnica della tua bici.',
          shellAny: 'Non lo so',
          speedsTitle: 'Velocità della trasmissione',
          speedsHelp: 'Scegli il numero di velocità della tua trasmissione.',
          crankTitle: 'Scegli le guarniture',
          crankHelp: 'Solo guarniture, senza kit completo: scegli o conferma lo spider e poi le corone.',
          spiderTitle: 'Spider, misuratore di potenza o corona Direct Mount',
          spiderHelp: 'Scegli l’interfaccia adatta alle guarniture. Una corona Direct Mount sostituisce spider, corone separate e viteria.',
          ringsTitle: 'Corone',
          ringsHelp: 'Corone da strada 110 BCD × 4 che si avvitano al tuo spider.',
          hardwareTitle: 'Viteria 2x',
          hardwareHelp: 'Per il montaggio servono viti 2x compatibili. Aggiungi il kit o conferma che riutilizzerai le tue.',
          ownBolts: 'Ho già viti 2x compatibili',
          ownBoltsHelp: 'Non aggiungeremo un kit di viti al carrello.',
          bbTitle: 'Movimento centrale (opzionale)',
          bbHelp: 'Opzionale: il montaggio è completo anche senza. Filtriamo in base al tuo asse e, se lo indichi, allo standard del movimento centrale del tuo telaio.',
          noneBb: 'Senza movimento centrale',
          summaryTitle: 'Il tuo crankset',
          flatView: 'Vista indicativa del montaggio dal lato delle corone.',
          flatBb: 'Il movimento centrale selezionato si monta dietro il gruppo.',
          flatLoading: 'Preparazione dell’immagine del tuo montaggio…',
          flatError: 'L’immagine del montaggio non è disponibile al momento.',
          included: 'Incluso con le guarniture',
          includedSpider: 'Queste guarniture hanno già lo spider',
          includedRings: 'Queste guarniture hanno già le corone',
          directMountIncluded: 'Integrata nella corona Direct Mount',
          notRequired: 'Non necessaria con Direct Mount',
          none: 'Nessuno',
          nothingHere: 'Nulla di compatibile in questo passaggio con quanto hai scelto finora.',
          next: 'Continua', back: 'Indietro', edit: 'Modifica',
          addBuild: 'Aggiungi il crankset al carrello', addShort: 'Aggiungi al carrello',
          adding: 'Aggiunta in corso…', added: 'Aggiunto! Apertura del carrello…',
          cartErr: 'Impossibile aggiungere. Riprova.',
          reset: 'Ricomincia', copy: 'Copia riepilogo', copied: 'Copiato!',
          total: 'Totale', weight: 'Peso indicativo', est: 'stimato',
          weightNote: 'Peso indicativo basato su dati pubblicati e stime documentate. Può variare in base a lunghezza delle guarniture, rapporto, viteria e tolleranze di produzione. Se riutilizzi viti compatibili tue, il calcolo esclude i 9,5 g del kit CYBREI.',
          weightUnavailable: 'Peso non verificato per questa configurazione',
          ready: 'Montaggio completo: le interfacce e le misure selezionate sono compatibili.',
          progress: 'Passaggio {current} di {total}',
          pricesLive: 'Prezzi del negozio aggiornati.', pricesCatalog: 'Prezzi di catalogo: ',
          parts: 'Componenti', spec: 'Scheda', from: 'da',
          blocked: 'Per aggiungere al carrello manca ancora:',
          slotArms: 'le guarniture', slotSpindle: 'l’asse', slotBb: 'il movimento centrale',
          slotSpider: 'lo spider', slotRings: 'le corone', slotBolts: 'la viteria 2x',
          specMm: 'Lunghezza', specSpindle: 'Asse', specSpeeds: 'Velocità', specShell: 'Telaio', specBcd: 'Interasse', specRing: 'Rapporto', specPm: 'Misuratore di potenza',
          yes: 'Sì', no: 'No',
          why: {
            needSpindle: 'Scegli prima lo standard dell’asse',
            needCrank: 'Scegli prima le guarniture',
            needSpider: 'Scegli prima uno spider',
            mount: 'Attacco diverso da quello delle tue guarniture',
            bcd: 'Interasse diverso: il tuo è {need}',
            family: 'Compatibile solo con {got}',
            unknown: 'Non possiamo confermare la compatibilità',
            spindle: 'Non disponibile con asse {need}',
            shell: 'Non disponibile per movimento centrale {need}',
            speeds: 'Non disponibile per {need} velocità',
            drivetrain: 'Non confermato per {need}',
            layout: 'Viteria non compatibile con queste corone',
            included: 'Già incluso con le guarniture',
            noRings: 'Non abbiamo corone compatibili',
          },
          propBuild: 'Crankset', propId: '_Crankset ID', propSpec: 'Configurazione',
          ariaSummary: 'Riepilogo del tuo montaggio', ariaProgress: 'Avanzamento del configuratore', ariaSteps: 'Passaggi',
          spindleStraight24: '24 mm dritto', spindleHollowtech: 'Tipo Hollowtech II · 24 mm dritto',
          shellItalian: 'Italiano', mountSram8: 'SRAM 8 fori', mountSram3: 'SRAM 3 fori',
        },
      };
      var T = I18N[LANG] || I18N.es;
      var LOCALE = { es: 'es-ES', en: 'en-GB', de: 'de-DE', it: 'it-IT', fr: 'fr-FR' }[LANG] || 'es-ES';

      /* ===================== live store prices ===================== */
      // The catalogue's prices are a dated copy. The store's own price for
      // each variant, in the shopper's market and currency, replaces it as
      // soon as it is read (assets/raceit-live-prices.js), so the cards, the
      // totals and the cart lines show what the checkout charges. The
      // catalogue's figure stays only where the store could not be read, and
      // the panel says so. B2B accounts keep the catalogue view, unchanged.
      var LP = window.RaceitLivePrices;
      var CAT_CUR = C.currency || 'EUR';
      var SHOP_CUR = root.getAttribute('data-cb-currency') || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CAT_CUR;
      var B2B = root.getAttribute('data-cb-b2b') === 'true', LIVE_ON = !!LP && !B2B;
      var PRICE_BASE = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var CAT_CENTS = {}, liveRow = {}, priceCur = CAT_CUR;
      Object.keys(P).forEach(function (h) { CAT_CENTS[h] = P[h].vs.map(function (r) { return r[1]; }); });
      function applyPrices(got) {
        var rows = [];
        Object.keys(P).forEach(function (h) {
          var e = got[h], byId = {};
          if (e && e.ok) e.variants.forEach(function (v) { byId[String(v.id)] = v; });
          P[h].vs.forEach(function (r, i) { var v = byId[String(r[0])]; rows.push([h, r, i, v && isFinite(v.price) ? v.price : null]); });
        });
        // Prices in another currency only when every one is known: the
        // catalogue's euros are never mixed into them.
        var useLive = SHOP_CUR === CAT_CUR || rows.every(function (x) { return x[3] != null; });
        liveRow = {};
        rows.forEach(function (x) {
          var live = useLive && x[3] != null;
          x[1][1] = live ? x[3] : CAT_CENTS[x[0]][x[2]];
          if (live) liveRow[x[0] + '|' + x[1][0]] = true;
        });
        priceCur = useLive ? SHOP_CUR : CAT_CUR;
      }
      function loadPrices() {
        if (!LIVE_ON) return;
        var known = LP.cached(PRICE_BASE);
        if (Object.keys(known).length) applyPrices(known);
        // Every variant row is priced, so a row beyond a product's first 250
        // variants is read on its own (raceit-live-prices.js).
        var needs = {};
        Object.keys(P).forEach(function (h) { needs[h] = P[h].vs.map(function (r) { return String(r[0]); }); });
        LP.load(Object.keys(P), PRICE_BASE, needs).then(function (got) {
          applyPrices(got);
          // A shopper mid-choice keeps their focus: the step repaints on the next pick.
          var stepEl = $('[data-cb-step]');
          if (stepEl && stepEl.contains(document.activeElement)) { renderPanel(); renderFlatSummary(); }
          else render();
        });
      }

      /* ========================== helpers ========================== */
      function eur(cents) {
        // Another market's currency, in the reader's language; euros keep
        // the builder's own "1.234,56 €".
        if (priceCur !== 'EUR') {
          try { return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: priceCur }).format(cents / 100); } catch (e) {}
        }
        var s = (cents / 100).toFixed(2).split('.');
        s[0] = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return s[0] + ',' + s[1] + ' €';
      }
      function grams(g) {
        if (typeof g !== 'number' || !isFinite(g)) return '—';
        return (g % 1 ? g.toFixed(1) : String(g)) + ' g';
      }
      function esc(s) {
        return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
          .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      }
      function $(sel) { return root.querySelector(sel); }
      function $all(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
      function whyText(why) {
        if (!why) return '';
        var t = T.why[why.code] || T.why.unknown;
        return t.replace('{need}', esc(labelOf(why.need))).replace('{got}', esc(labelOf(why.got)));
      }
      /* Axle diameter and drivetrain compatibility are separate. Keep the
         labels mechanical: a straight 24 mm axle is not SRAM GXP (24/22 mm).

         Built from T (the resolved locale's I18N object, already in scope) so
         these read correctly in every language instead of always showing the
         Spanish word — this dictionary used to be hardcoded Spanish regardless
         of storefront locale ("Italiano", "recto", "pernos" leaked into every
         non-es render). The standard codes themselves (DUB, BSA, T47…) are
         international and stay as-is. */
      var SPINDLE_LABEL = { dub: 'DUB 28,99 mm', 24: T.spindleStraight24, 30: '30 mm' };
      var SPINDLE_SHORT = { dub: 'DUB', 24: '24 mm', 30: '30 mm' };
      var SPINDLE_NOTE = { dub: 'DUB · 28,99 mm', 24: T.spindleHollowtech, 30: '30 mm' };
      var SHELL_LABEL = { BSA: 'BSA', ITA: T.shellItalian, T47: 'T47', BB386: 'BB386', PF: 'BB86 (Press Fit)' };
      var MOUNT_LABEL = { cinch: 'Easton/RaceFace CINCH', 'sram-8b': T.mountSram8, 'sram-3b': T.mountSram3, 'shimano-dm': 'Shimano', 'cannondale-si': 'Cannondale', specialized: 'Specialized' };
      function labelOf(v) {
        if (v == null) return '';
        return SPINDLE_LABEL[v] || SHELL_LABEL[v] || MOUNT_LABEL[v] || String(v);
      }

      /* ================ catalog option names/values i18n ================
         window.CBCatalog (assets/crank-builder-catalog.js) is generated from
         the live product feed, which only carries the shop's primary
         language (Spanish) — build-crank-builder-catalog.cjs reads
         products.json, not a per-locale translation. So the option names
         (p.opts, e.g. "Desarrollo") and values (e.g. "Negro") arriving from
         the catalog are always Spanish, regardless of storefront locale.

         This is a closed, reused vocabulary (colours, "Type", "Model"…), so a
         string-keyed dictionary — same shape as SPINDLE_LABEL/SHELL_LABEL
         above — covers it without per-product coupling. Falls through to the
         original Spanish string for es and for anything not in the table
         (a new option name/value added to the catalog shows in Spanish until
         someone adds it here, rather than breaking).

         Product TITLES (p.t) are NOT covered here — see the comment on
         cardHtml() below. */
      var OPT_NAME_TR = {
        en: {
          'Añadir Araña': 'Add spider', 'Color': 'Colour', 'Compatibilidad': 'Compatibility',
          'Compatibilidad AXO': 'AXO compatibility', 'Desarrollo': 'Gearing', 'Eje': 'Axle',
          'Eje compatible': 'Compatible axle', 'Estilo logo': 'Logo style', 'Longitud de biela': 'Crank length',
          'Medida': 'Length', 'Modelo': 'Model', 'Potenciometro': 'Power meter', 'Tamaño': 'Size',
          'Tipo': 'Type', 'Tipo de logo': 'Logo type', 'Versión': 'Version',
        },
        de: {
          'Añadir Araña': 'Spider hinzufügen', 'Color': 'Farbe', 'Compatibilidad': 'Kompatibilität',
          'Compatibilidad AXO': 'AXO-Kompatibilität', 'Desarrollo': 'Übersetzung', 'Eje': 'Achse',
          'Eje compatible': 'Kompatible Achse', 'Estilo logo': 'Logo-Stil', 'Longitud de biela': 'Kurbellänge',
          'Medida': 'Länge', 'Modelo': 'Modell', 'Potenciometro': 'Leistungsmesser', 'Tamaño': 'Größe',
          'Tipo': 'Typ', 'Tipo de logo': 'Logo-Typ', 'Versión': 'Version',
        },
        fr: {
          'Añadir Araña': 'Ajouter une araignée', 'Color': 'Couleur', 'Compatibilidad': 'Compatibilité',
          'Compatibilidad AXO': 'Compatibilité AXO', 'Desarrollo': 'Développement', 'Eje': 'Axe',
          'Eje compatible': 'Axe compatible', 'Estilo logo': 'Style de logo', 'Longitud de biela': 'Longueur de manivelle',
          'Medida': 'Longueur', 'Modelo': 'Modèle', 'Potenciometro': 'Capteur de puissance', 'Tamaño': 'Taille',
          'Tipo': 'Type', 'Tipo de logo': 'Type de logo', 'Versión': 'Version',
        },
        it: {
          'Añadir Araña': 'Aggiungi spider', 'Color': 'Colore', 'Compatibilidad': 'Compatibilità',
          'Compatibilidad AXO': 'Compatibilità AXO', 'Desarrollo': 'Rapporto', 'Eje': 'Asse',
          'Eje compatible': 'Asse compatibile', 'Estilo logo': 'Stile del logo', 'Longitud de biela': 'Lunghezza guarnitura',
          'Medida': 'Lunghezza', 'Modelo': 'Modello', 'Potenciometro': 'Misuratore di potenza', 'Tamaño': 'Taglia',
          'Tipo': 'Tipo', 'Tipo de logo': 'Tipo di logo', 'Versión': 'Versione',
        },
      };
      var OPT_VAL_TR = {
        en: {
          'Anodizada dura': 'Hard anodised', 'Azul': 'Blue', 'Doble plato': 'Double chainring',
          'Logo blanco grande': 'Large white logo', 'Logo blanco pequeño': 'Small white logo',
          'Logo rojo pequeño': 'Small red logo', 'Morado': 'Purple', 'Negro': 'Black', 'Plata': 'Silver',
          'Rojo': 'Red', 'Rosa': 'Pink', 'Sin araña': 'Without spider', 'Naranja': 'Orange', 'Amarillo': 'Yellow',
        },
        de: {
          'Anodizada dura': 'Hart eloxiert', 'Azul': 'Blau', 'Doble plato': 'Doppel-Kettenblatt',
          'Logo blanco grande': 'Großes weißes Logo', 'Logo blanco pequeño': 'Kleines weißes Logo',
          'Logo rojo pequeño': 'Kleines rotes Logo', 'Morado': 'Lila', 'Negro': 'Schwarz', 'Plata': 'Silber',
          'Rojo': 'Rot', 'Rosa': 'Pink', 'Sin araña': 'Ohne Spider', 'Naranja': 'Orange', 'Amarillo': 'Gelb',
        },
        fr: {
          'Anodizada dura': 'Anodisé dur', 'Azul': 'Bleu', 'Doble plato': 'Double plateau',
          'Logo blanco grande': 'Grand logo blanc', 'Logo blanco pequeño': 'Petit logo blanc',
          'Logo rojo pequeño': 'Petit logo rouge', 'Morado': 'Violet', 'Negro': 'Noir', 'Plata': 'Argent',
          'Rojo': 'Rouge', 'Rosa': 'Rose', 'Sin araña': 'Sans araignée', 'Naranja': 'Orange', 'Amarillo': 'Jaune',
        },
        it: {
          'Anodizada dura': 'Anodizzato duro', 'Azul': 'Blu', 'Doble plato': 'Doppia corona',
          'Logo blanco grande': 'Logo bianco grande', 'Logo blanco pequeño': 'Logo bianco piccolo',
          'Logo rojo pequeño': 'Logo rosso piccolo', 'Morado': 'Viola', 'Negro': 'Nero', 'Plata': 'Argento',
          'Rojo': 'Rosso', 'Rosa': 'Rosa', 'Sin araña': 'Senza spider', 'Naranja': 'Arancione', 'Amarillo': 'Giallo',
        },
      };
      function trOptName(s) { var d = OPT_NAME_TR[LANG]; return (d && d[s]) || s; }
      function trOptVal(s) { var d = OPT_VAL_TR[LANG]; return (d && d[s]) || s; }
      // rules.js (kept locale-free on purpose — it's shared with the Node-side
      // compatibility assertions) joins selected values as "150 mm · Negro".
      // Split back apart to translate each token, then rejoin.
      function trSub(sub) { return sub ? sub.split(' · ').map(trOptVal).join(' · ') : sub; }

      /* Product titles (p.t), keyed by handle rather than the Spanish string
         itself — a title is not reused vocabulary the way "Negro" or "Tipo"
         is, so each of the 24 parts in the catalog needs its own entry.

         Maintenance note: build-crank-builder-catalog.cjs regenerates
         crank-builder-catalog.js from the live product feed. If a title
         changes or a new part is added, this table does NOT update itself —
         an untranslated title just falls back to Spanish (fails visible, not
         broken), the same behaviour as the rest of this file. Whoever
         maintains the crank-builder catalog should extend TITLE_TR when they
         add or rename a part. */
      var TITLE_TR = {
        en: {
          'arana-estandar-cybrei': 'Cybrei Standard Spider',
          'arana-neutron-de-aluminio': 'Neutron Aluminium Spider',
          'bielas-de-carbono-neutron': 'Neutron Carbon Crank Arms',
          'caja-de-pedalier': 'Cybrei Bottom Bracket',
          'cybrei-2x-chairing-bolt-kit': 'Cybrei Chainring Bolts',
          'cybrei-arana-bcd144x5': 'Cybrei Spider BCD 144x5',
          'cybrei-carbon-crank-assembly': 'Cybrei Carbon Crank Arms',
          'cybrei-carbon-crankset': 'Cybrei Carbon Crank Arms (Complete)',
          'cybrei-ultra-bielas-de-carbono': 'Cybrei Ultra Carbon Crank Arms',
          'cybrei-ultra-bielas-de-carbono-conjunto': 'Cybrei Ultra Carbon Crank Arms (Complete)',
          'gemini-rigel-plato-carbono-carretera-gravel': 'Gemini Rigel Carbon Road & Gravel Chainring',
          'overfast-aero-carbon-crankarm': 'Overfast Aero Carbon Crank Arms',
          'overfast-light-carbon-crankarm': 'Overfast Light Carbon Crank Arms',
          'platos-evo-cybrei': 'Cybrei EVO PRO Chainrings',
          'platos-metarbon-aero-2x-cybrei': 'Cybrei Metarbon Aero 2x Chainrings',
          'platos-neutron-de-carbono': 'Neutron Carbon Chainrings',
          'platos-overfast': 'Overfast Chainrings',
          'platos-y-bielas-neutron-con-potenciometro': 'Neutron Chainrings & Crank Arms with Power Meter',
          'potenciometro-sigey-axo': 'Sigeyi AXO Power Meter',
        },
        de: {
          'arana-estandar-cybrei': 'Cybrei Standard-Spider',
          'arana-neutron-de-aluminio': 'Neutron Aluminium-Spider',
          'bielas-de-carbono-neutron': 'Neutron Carbon-Kurbeln',
          'caja-de-pedalier': 'Cybrei Innenlager',
          'cybrei-2x-chairing-bolt-kit': 'Cybrei Kettenblattschrauben',
          'cybrei-arana-bcd144x5': 'Cybrei Spider BCD 144x5',
          'cybrei-carbon-crank-assembly': 'Cybrei Carbon-Kurbeln',
          'cybrei-carbon-crankset': 'Cybrei Carbon-Kurbeln (Komplett)',
          'cybrei-ultra-bielas-de-carbono': 'Cybrei Ultra Carbon-Kurbeln',
          'cybrei-ultra-bielas-de-carbono-conjunto': 'Cybrei Ultra Carbon-Kurbeln (Komplett)',
          'gemini-rigel-plato-carbono-carretera-gravel': 'Gemini Rigel Carbon-Kettenblatt Road & Gravel',
          'overfast-aero-carbon-crankarm': 'Overfast Aero-Carbon-Kurbeln',
          'overfast-light-carbon-crankarm': 'Overfast Light-Carbon-Kurbeln',
          'platos-evo-cybrei': 'Cybrei EVO PRO Kettenblätter',
          'platos-metarbon-aero-2x-cybrei': 'Cybrei Metarbon Aero 2x Kettenblätter',
          'platos-neutron-de-carbono': 'Neutron Carbon-Kettenblätter',
          'platos-overfast': 'Overfast Kettenblätter',
          'platos-y-bielas-neutron-con-potenciometro': 'Neutron Kettenblätter & Kurbeln mit Leistungsmesser',
          'potenciometro-sigey-axo': 'Sigeyi AXO Leistungsmesser',
        },
        fr: {
          'arana-estandar-cybrei': 'Araignée standard Cybrei',
          'arana-neutron-de-aluminio': 'Araignée Neutron en aluminium',
          'bielas-de-carbono-neutron': 'Manivelles Neutron en carbone',
          'caja-de-pedalier': 'Boîtier de pédalier Cybrei',
          'cybrei-2x-chairing-bolt-kit': 'Boulonnerie de plateaux Cybrei',
          'cybrei-arana-bcd144x5': 'Araignée Cybrei BCD 144x5',
          'cybrei-carbon-crank-assembly': 'Manivelles Cybrei en carbone',
          'cybrei-carbon-crankset': 'Manivelles Cybrei en carbone (Kit complet)',
          'cybrei-ultra-bielas-de-carbono': 'Manivelles Cybrei Ultra en carbone',
          'cybrei-ultra-bielas-de-carbono-conjunto': 'Manivelles Cybrei Ultra en carbone (Kit complet)',
          'gemini-rigel-plato-carbono-carretera-gravel': 'Plateau carbone Gemini Rigel route et gravel',
          'overfast-aero-carbon-crankarm': 'Manivelles Overfast Aero Carbon',
          'overfast-light-carbon-crankarm': 'Manivelles Overfast Light Carbon',
          'platos-evo-cybrei': 'Plateaux Cybrei EVO PRO',
          'platos-metarbon-aero-2x-cybrei': 'Plateaux Cybrei Metarbon Aero 2x',
          'platos-neutron-de-carbono': 'Plateaux Neutron en carbone',
          'platos-overfast': 'Plateaux Overfast',
          'platos-y-bielas-neutron-con-potenciometro': 'Plateaux et manivelles Neutron avec capteur de puissance',
          'potenciometro-sigey-axo': 'Capteur de puissance Sigeyi AXO',
        },
        it: {
          'arana-estandar-cybrei': 'Spider standard Cybrei',
          'arana-neutron-de-aluminio': 'Spider Neutron in alluminio',
          'bielas-de-carbono-neutron': 'Guarniture Neutron in carbonio',
          'caja-de-pedalier': 'Movimento centrale Cybrei',
          'cybrei-2x-chairing-bolt-kit': 'Viteria corone Cybrei',
          'cybrei-arana-bcd144x5': 'Spider Cybrei BCD 144x5',
          'cybrei-carbon-crank-assembly': 'Guarniture Cybrei in carbonio',
          'cybrei-carbon-crankset': 'Guarniture Cybrei in carbonio (Kit completo)',
          'cybrei-ultra-bielas-de-carbono': 'Guarniture Cybrei Ultra in carbonio',
          'cybrei-ultra-bielas-de-carbono-conjunto': 'Guarniture Cybrei Ultra in carbonio (Kit completo)',
          'gemini-rigel-plato-carbono-carretera-gravel': 'Corona carbonio Gemini Rigel strada e gravel',
          'overfast-aero-carbon-crankarm': 'Guarniture Overfast Aero Carbon',
          'overfast-light-carbon-crankarm': 'Guarniture Overfast Light Carbon',
          'platos-evo-cybrei': 'Corone Cybrei EVO PRO',
          'platos-metarbon-aero-2x-cybrei': 'Corone Cybrei Metarbon Aero 2x',
          'platos-neutron-de-carbono': 'Corone Neutron in carbonio',
          'platos-overfast': 'Corone Overfast',
          'platos-y-bielas-neutron-con-potenciometro': 'Corone e guarniture Neutron con misuratore di potenza',
          'potenciometro-sigey-axo': 'Misuratore di potenza Sigeyi AXO',
        },
      };
      function trTitle(h) {
        var d = TITLE_TR[LANG];
        return (d && d[h]) || (P[h] && P[h].t) || h;
      }

      var TICK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>';
      var LOCK = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';

      /* =========================== state =========================== */
      var S = R.freshState();
      var step = 0;
      var STEP_KEYS = R.STEPS; // spindle, crank, spider, rings, bb, summary

      function stepSkipped(i) {
        var ck = S.crank ? R.eff(S.crank, S.sel[S.crank]) : null;
        var sp = S.spider ? R.eff(S.spider, S.sel[S.spider]) : null;
        if (i === 2) return !!(ck && ck.includes.spider);   // e.g. Neutron "Con araña"
        if (i === 3) return !!(ck && ck.includes.rings) || !!(sp && sp.includes.rings);
        if (i === 4) return !!S.spindle && !R.byRole.bb.some(function (h) { return R.partAllowed(h, S); });
        return false;
      }
      /* The axle standard opens the crank step; the arms open everything after
         it. The frame shell and the bottom bracket gate nothing. */
      function stepEnabled(i) {
        if (i === 0) return true;
        if (i === 1) return !!S.spindle;
        return !!S.crank;
      }
      function nextStep(from) { for (var i = from + 1; i < STEP_KEYS.length; i++) if (!stepSkipped(i)) return i; return STEP_KEYS.length - 1; }
      function prevStep(from) { for (var i = from - 1; i >= 0; i--) if (!stepSkipped(i)) return i; return 0; }

      /* ===================== option pills ===================== */
      function optionsHtml(h) {
        var p = P[h];
        if (!p.opts.length) return '';
        var sel = S.sel[h] || [];
        var out = '';
        p.opts.forEach(function (name, i) {
          var vals = R.valuesFor(h, i, S);
          // Nothing to ask when only one value survives — it is already chosen
          // for them (the Eje option after they picked the axle standard, say).
          if (vals.length <= 1) return;
          out += '<div class="cb-optrow"><div class="cb-optrow__lbl">' + esc(trOptName(name)) + '</div><div class="cb-pills" role="group" aria-label="' + esc(trOptName(name)) + '">';
          vals.forEach(function (v) {
            var on = sel[i] === v;
            // data-cb-val stays the raw Spanish value — it is matched against
            // the catalog's own variant data — only the button's visible text
            // is translated.
            out += '<button type="button" class="cb-pill' + (on ? ' is-on' : '') + '"' +
              ' data-cb-opt="' + esc(h) + '" data-cb-idx="' + i + '" data-cb-val="' + esc(v) + '"' +
              ' aria-pressed="' + (on ? 'true' : 'false') + '">' + esc(trOptVal(v)) + '</button>';
          });
          out += '</div></div>';
        });
        return out;
      }

      /* ====================== part cards ====================== */
      function cardWeight(h, rows, checked) {
        var values;
        if (checked) {
          var selected = R.resolveRow(h, S.sel[h] || []);
          var exact = R.weightOfRow(h, selected);
          return exact == null ? '' : '≈ ' + grams(exact);
        }
        if (!rows.length) return '';
        values = rows.map(function (row) { return R.weightOfRow(h, row); });
        // A range based on only the known variants would imply that the
        // unknown variants fall inside it, so show nothing unless all are known.
        if (values.some(function (value) { return value == null; })) return '';
        var min = Math.min.apply(null, values), max = Math.max.apply(null, values);
        return '≈ ' + grams(min) + (max !== min ? '–' + grams(max) : '');
      }
      function cardHtml(h, role, checked) {
        var p = P[h];
        var why = R.whyDisabled(h, S);
        var off = !!why;
        var rows = off ? [] : R.compatibleRows(h, S);
        var from = rows.length ? Math.min.apply(null, rows.map(function (r) { return r[1]; })) : null;
        return '<label class="cb-opt' + (off ? ' is-off' : '') + (checked ? ' is-on' : '') + '">' +
          '<input type="radio" name="cb-' + role + '" value="' + esc(h) + '" data-cb-pick="' + role + '"' +
          (checked ? ' checked' : '') + (off ? ' disabled' : '') + '>' +
          '<span class="cb-opt__card">' +
            '<span class="cb-opt__row">' +
              (p.img ? '<img class="cb-opt__img" loading="lazy" src="' + esc(p.img) + '" alt="' + esc(trTitle(h)) + '" width="64" height="52">' : '<span class="cb-opt__img cb-opt__img--none" aria-hidden="true"></span>') +
              '<span class="cb-opt__body">' +
                '<span class="cb-opt__brand">' + esc(p.b) + '</span>' +
                '<span class="cb-opt__name">' + esc(trTitle(h)) + '</span>' +
                (off ? '<span class="cb-opt__why">' + LOCK + ' ' + esc(whyText(why)) + '</span>' : '') +
              '</span>' +
              '<span class="cb-opt__tick" aria-hidden="true">' + TICK + '</span>' +
            '</span>' +
            '<span class="cb-opt__meta">' +
              '<span class="cb-opt__g">' + esc(cardWeight(h, rows, checked)) + '</span>' +
              '<span class="cb-opt__p">' + (from != null ? T.from + ' ' + eur(from) : '') + '</span>' +
            '</span>' +
          '</span></label>';
      }
      function pseudoCard(role, label, sub, checked, value) {
        return '<label class="cb-opt cb-opt--pseudo' + (checked ? ' is-on' : '') + '">' +
          '<input type="radio" name="cb-' + role + '" value="' + esc(value || '') + '" data-cb-pick="' + role + '"' + (checked ? ' checked' : '') + '>' +
          '<span class="cb-opt__card"><span class="cb-opt__row"><span class="cb-opt__body">' +
            '<span class="cb-opt__name">' + esc(label) + '</span>' +
            (sub ? '<span class="cb-opt__brand">' + esc(sub) + '</span>' : '') +
          '</span><span class="cb-opt__tick" aria-hidden="true">' + TICK + '</span></span></span></label>';
      }
      function listHtml(role, current, allowNone, noneLabel) {
        var list = R.byRole[role] || [];
        var html = '';
        if (allowNone) html += pseudoCard(role, noneLabel || T.none, '', !current);
        list.forEach(function (h) { html += cardHtml(h, role, current === h); });
        var anyOn = list.some(function (h) { return !R.whyDisabled(h, S); });
        if (!anyOn && !allowNone) html += '<p class="cb-empty">' + esc(T.nothingHere) + '</p>';
        return html;
      }
      function hardwareListHtml() {
        var html = pseudoCard('bolts', T.ownBolts, T.ownBoltsHelp, S.boltsOwn, '__own');
        R.byRole.bolts.forEach(function (h) { html += cardHtml(h, 'bolts', !S.boltsOwn && S.bolts === h); });
        return html;
      }

      /* ========================= steps ========================= */
      function renderStep() {
        var el = $('[data-cb-step]');
        var ck = S.crank ? R.eff(S.crank, S.sel[S.crank]) : null;
        var html = '';

        if (step === 0) {
          html += head(T.spindleTitle, T.spindleHelp);
          html += '<div class="cb-block"><div class="cb-pills cb-pills--wide">';
          R.spindles().forEach(function (s) {
            var on = S.spindle === s;
            html += '<button type="button" class="cb-pill' + (on ? ' is-on' : '') + '" data-cb-spindle="' + esc(s) + '"' +
              ' aria-pressed="' + (on ? 'true' : 'false') + '">' + esc(SPINDLE_SHORT[s] || s) +
              ' <small>' + esc(SPINDLE_NOTE[s] || '') + '</small></button>';
          });
          html += '</div></div>';
        } else if (step === 1) {
          html += head(T.crankTitle, T.crankHelp);
          html += '<div class="cb-list">' + listHtml('crank', S.crank, false) + '</div>';
          if (S.crank) html += '<div class="cb-block">' + optionsHtml(S.crank) + '</div>';
        } else if (step === 2) {
          html += head(T.spiderTitle, T.spiderHelp);
          if (ck && ck.includes.spider) html += '<p class="cb-note">' + esc(T.includedSpider) + '</p>';
          else {
            html += '<div class="cb-list">' + listHtml('spider', S.spider, false) + '</div>';
            if (S.spider) html += '<div class="cb-block">' + optionsHtml(S.spider) + '</div>';
          }
        } else if (step === 3) {
          html += head(T.ringsTitle, T.ringsHelp);
          if (ck && ck.includes.rings) html += '<p class="cb-note">' + esc(T.includedRings) + '</p>';
          else {
            html += '<div class="cb-block"><div class="cb-optrow__lbl">' + esc(T.speedsTitle) + '</div><div class="cb-pills">';
            [11, 12].forEach(function (n) {
              html += '<button type="button" class="cb-pill' + (S.speeds === n ? ' is-on' : '') + '" data-cb-speeds="' + n + '"' +
                ' aria-pressed="' + (S.speeds === n ? 'true' : 'false') + '">' + n + 'v</button>';
            });
            html += '</div><p class="cb-step__help">' + esc(T.speedsHelp) + '</p></div>';
            html += '<div class="cb-list">' + listHtml('rings', S.rings, false) + '</div>';
            if (S.rings) html += '<div class="cb-block">' + optionsHtml(S.rings) + '</div>';
            if (S.rings) {
              html += '<h3 class="cb-step__subhead">' + esc(T.hardwareTitle) + '</h3><p class="cb-step__help">' + esc(T.hardwareHelp) + '</p>';
              html += '<div class="cb-list">' + hardwareListHtml() + '</div>';
              if (S.bolts) html += '<div class="cb-block">' + optionsHtml(S.bolts) + '</div>';
            }
          }
        } else if (step === 4) {
          // Optional: the shell question lives here because narrowing the
          // bottom bracket is the only thing it is ever used for.
          html += head(T.bbTitle, T.bbHelp);
          html += '<div class="cb-block"><div class="cb-optrow__lbl">' + esc(T.shellTitle) + '</div><div class="cb-pills cb-pills--wide">';
          html += '<button type="button" class="cb-pill' + (S.shell ? '' : ' is-on') + '" data-cb-shell=""' +
            ' aria-pressed="' + (S.shell ? 'false' : 'true') + '">' + esc(T.shellAny) + '</button>';
          R.shells().forEach(function (s) {
            html += '<button type="button" class="cb-pill' + (S.shell === s ? ' is-on' : '') + '" data-cb-shell="' + esc(s) + '"' +
              ' aria-pressed="' + (S.shell === s ? 'true' : 'false') + '">' + esc(SHELL_LABEL[s] || s) + '</button>';
          });
          html += '</div><p class="cb-step__help">' + esc(T.shellHelp) + '</p></div>';
          html += '<div class="cb-list">' + listHtml('bb', S.bb, true, T.noneBb) + '</div>';
          if (S.bb) html += '<div class="cb-block">' + optionsHtml(S.bb) + '</div>';
        } else {
          html += head(T.summaryTitle, '');
          html += summaryHtml();
        }
        el.innerHTML = html;

        // nav
        var nav = $('[data-cb-nav]');
        var isLast = step === STEP_KEYS.length - 1;
        nav.innerHTML =
          (step > 0 ? '<button type="button" class="cb-btn cb-btn--ghost" data-cb-back>' + esc(T.back) + '</button>' : '<span></span>') +
          (isLast ? '' : '<button type="button" class="cb-btn cb-btn--primary" data-cb-next' + (stepEnabled(nextStep(step)) ? '' : ' disabled') + '>' + esc(T.next) + '</button>');
      }
      function head(title, help) {
        return '<h2 class="cb-step__title">' + esc(title) + '</h2>' + (help ? '<p class="cb-step__help">' + esc(help) + '</p>' : '');
      }

      /* ======================== summary ======================== */
      function slotLabel(slot) {
        return { arms: T.slotArms, spindle: T.slotSpindle, bb: T.slotBb, spider: T.slotSpider, rings: T.slotRings, bolts: T.slotBolts }[slot] || slot;
      }
      function summaryHtml() {
        var sum = R.summary(S);
        var html = '';
        if (!sum.issues.length && flatApi) {
          html += '<figure class="cb-final" data-cb-final><div class="cb-final__canvas" data-cb-flat>' +
            '<p class="cb-final__status" role="status">' + esc(T.flatLoading) + '</p></div>' +
            '<figcaption class="cb-final__caption">' + esc(T.flatView) + '</figcaption>';
          var bbLine = sum.lines.find(function (line) { return line.handle === S.bb; });
          if (bbLine) html += '<div class="cb-final__bb">' +
            (bbLine.img ? '<img src="' + esc(bbLine.img) + '" alt="' + esc(trTitle(bbLine.handle)) + '" width="72" height="56">' : '') +
            '<span><b>' + esc(trTitle(bbLine.handle)) + '</b><span>' + esc(trSub(bbLine.sub || '')) + '</span><span>' + esc(T.flatBb) + '</span></span></div>';
          html += '</figure>';
        }
        html += '<div class="cb-sum">';
        html += '<div class="cb-sum__h">' + esc(T.parts) + '</div><ul class="cb-sum__list">';
        sum.lines.forEach(function (l) {
          html += '<li class="cb-sum__li">' +
            (l.img ? '<img loading="lazy" src="' + esc(l.img) + '" alt="' + esc(trTitle(l.handle)) + '" width="48" height="40">' : '') +
            '<span class="cb-sum__body"><a class="cb-sum__name" href="' + esc(l.url) + '">' + esc(trTitle(l.handle)) + '</a>' +
            (l.sub ? '<span class="cb-sum__sub">' + esc(trSub(l.sub)) + '</span>' : '') + '</span>' +
            '<span class="cb-sum__p">' + eur(l.cents) + '</span></li>';
        });
        html += '</ul>';
        if (sum.spec) {
          html += '<div class="cb-sum__h">' + esc(T.spec) + '</div><dl class="cb-spec">';
          function row(k, v) { if (v) html += '<div><dt>' + esc(k) + '</dt><dd>' + esc(v) + '</dd></div>'; }
          row(T.specMm, sum.spec.mm ? sum.spec.mm + ' mm' : null);
          row(T.specSpindle, labelOf(sum.spec.spindle));
          row(T.specSpeeds, sum.spec.speeds ? sum.spec.speeds + 'v' : null);
          row(T.specShell, labelOf(sum.spec.shell));
          row(T.specBcd, sum.spec.bcd ? sum.spec.bcd.replace('x', ' × ') : null);
          row(T.specRing, sum.spec.ring ? sum.spec.ring.label : null);
          row(T.specPm, sum.spec.pm ? T.yes : null);
          html += '</dl>';
        }
        html += '<p class="cb-weight-note cb-weight-note--summary">' + esc(T.weightNote) + '</p>';
        html += '</div>';
        return html;
      }

      /* ===================== expected delivery ===================== */
      // A crankset ships when its slowest part arrives: each product's own
      // estimate (metafield custom.tiempo_entrega_estimado, read live through
      // /products/<handle>?view=leadtime), the slowest one shown. Shared with
      // the Bike Studio: assets/raceit-lead-time.js.
      var LT = window.RaceitLeadTime, leadKey = null, leadSeq = 0;
      var TRUCK = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M1.5 5.5h12v10h-12zM13.5 9h4.5l3 3.5v3h-7.5"/><circle cx="6" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/></svg>';
      function paintLead(s) {
        var w = LT.words(LANG), text = s ? LT.format(s.lead, LANG) : '';
        $all('[data-cb-lead]').forEach(function (el) {
          el.hidden = !text;
          el.innerHTML = text ? TRUCK + '<span class="cb-lead__body"><span class="cb-lead__lbl">' + esc(w[2]) + '</span>' +
            '<span class="cb-lead__val">' + esc(text) + '</span><span class="cb-lead__by">' + esc(w[5] + ': ' + s.label) + '</span></span>' : '';
        });
        $all('[data-cb-lead-short]').forEach(function (el) { el.hidden = !text; el.textContent = text ? w[3] + ': ' + text : ''; });
      }
      function refreshLead(sum) {
        if (!LT) return;
        var items = sum.lines.filter(function (l) { return l.handle; }).map(function (l) { return { handle: l.handle, label: trTitle(l.handle) }; });
        var key = items.map(function (i) { return i.handle; }).join('|');
        if (key === leadKey) return;
        leadKey = key;
        var ticket = ++leadSeq;
        if (!items.length) { paintLead(null); return; }
        var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        LT.forBuild(items, base).then(function (r) { if (ticket === leadSeq) paintLead(r.slowest); });
      }

      /* ===================== live totals panel ===================== */
      function renderPanel() {
        var sum = R.summary(S);
        $all('[data-cb-total]').forEach(function (el) { el.textContent = sum.lines.length ? eur(sum.cents) : '—'; });
        var priceNote = $('[data-cb-price-note]');
        if (priceNote) {
          var fromStore = sum.lines.length && sum.lines.every(function (l) { return liveRow[l.handle + '|' + l.id]; });
          priceNote.hidden = !sum.lines.length || B2B;
          priceNote.textContent = fromStore ? T.pricesLive : T.pricesCatalog + String(C.generatedAt || '').slice(0, 10);
        }
        $all('[data-cb-grams]').forEach(function (el) { el.textContent = sum.weightComplete ? '≈ ' + grams(sum.grams) : '—'; });
        refreshLead(sum);

        // the running parts list
        var list = $('[data-cb-panel-list]');
        var ck = S.crank ? R.eff(S.crank, S.sel[S.crank]) : null;
        var sp = S.spider ? R.eff(S.spider, S.sel[S.spider]) : null;
        var rows = [
          ['arms', T.steps[1], S.crank, 1],
          ['spider', T.steps[2], (ck && ck.includes.spider) ? '__inc' : S.spider, 2],
          ['rings', T.steps[3], (ck && ck.includes.rings) ? '__inc' : ((sp && sp.includes.rings) ? '__dm' : S.rings), 3],
          ['bolts', T.hardwareTitle, (sp && sp.includes.rings) ? '__na' : ((ck && ck.includes.bolts) ? '__inc' : (S.boltsOwn ? '__own' : S.bolts)), 3],
          ['bb', T.steps[4], S.bb, 4],
        ];
        list.innerHTML = rows.map(function (r) {
          var val = r[2] === '__inc' ? T.included : (r[2] === '__dm' ? T.directMountIncluded : (r[2] === '__na' ? T.notRequired : (r[2] === '__own' ? T.ownBolts : (r[2] && P[r[2]] ? trTitle(r[2]) : '—'))));
          var sub = (r[2] && r[2].slice(0, 2) !== '__' && S.sel[r[2]]) ? (S.sel[r[2]].filter(Boolean).map(trOptVal).join(' · ')) : '';
          return '<li class="cb-panel__li' + (r[2] ? ' is-set' : '') + '">' +
            '<button type="button" class="cb-panel__jump" data-cb-goto="' + r[3] + '">' +
              '<span class="cb-panel__lbl">' + esc(r[1]) + '</span>' +
              '<span class="cb-panel__val">' + esc(val) + (sub ? ' <small>' + esc(sub) + '</small>' : '') + '</span>' +
            '</button></li>';
        }).join('');

        // blocking issues + cart button
        var issues = sum.issues;
        var msg = $('[data-cb-issues]');
        var seen = {}, names = [];
        issues.forEach(function (i) { if (!seen[i.slot]) { seen[i.slot] = 1; names.push(slotLabel(i.slot)); } });
        if (names.length) {
          msg.classList.remove('cb-issues--ok');
          msg.hidden = false;
          msg.innerHTML = '<strong>' + esc(T.blocked) + '</strong> ' + esc(names.join(', '));
        } else {
          msg.classList.add('cb-issues--ok');
          msg.hidden = false;
          msg.textContent = T.ready;
        }
        $all('[data-cb-cart]').forEach(function (b) { b.disabled = !!names.length; });

        // rail
        $all('[data-cb-railbtn]').forEach(function (b) {
          var i = +b.dataset.cbRailbtn;
          b.classList.toggle('is-on', i === step);
          b.hidden = stepSkipped(i);
          b.disabled = !stepEnabled(i);
          if (i === step) b.setAttribute('aria-current', 'step');
          else b.removeAttribute('aria-current');
        });

        var visible = $all('[data-cb-railbtn]').filter(function (b) { return !b.hidden; });
        var active = visible.findIndex(function (b) { return +b.dataset.cbRailbtn === step; });
        var current = active < 0 ? 1 : active + 1;
        var progress = $('[data-cb-progress]');
        var progressText = $('[data-cb-progress-text]');
        var progressFill = $('[data-cb-progress-fill]');
        if (progress) {
          progress.setAttribute('aria-valuemax', String(visible.length));
          progress.setAttribute('aria-valuenow', String(current));
        }
        if (progressText) progressText.textContent = T.progress.replace('{current}', current).replace('{total}', visible.length);
        if (progressFill) progressFill.style.width = (current / visible.length * 100) + '%';
      }

      var flatApi = window.CBFlat && window.CBVisual ? window.CBFlat.create({
        visual: window.CBVisual, catalog: C, rules: R, title: trTitle, label: T.summaryTitle,
        assetBase: root.getAttribute('data-cb-asset-base'),
      }) : null;
      function renderFlatSummary() {
        var flatHost = $('[data-cb-flat]');
        root.classList.toggle('is-summary', !!flatHost);
        if (flatHost) Promise.resolve().then(function () {
          return flatApi.render(flatHost, S);
        }).catch(function () {
          if (!flatHost.isConnected) return;
          flatHost.textContent = T.flatError;
          flatHost.setAttribute('role', 'status');
        });
      }

      function render() { renderStep(); renderPanel(); renderFlatSummary(); }

      /* ========================== cart ========================== */
      function addToCart() {
        var sum = R.summary(S);
        if (sum.issues.length) return;
        var btns = $all('[data-cb-cart]');
        btns.forEach(function (b) { b.disabled = true; });
        var msg = $('[data-cb-cartmsg]');
        msg.textContent = T.adding;

        var cid = 'CK-' + Date.now().toString(36).toUpperCase();
        var spec = sum.spec || {};
        var bits = [];
        if (spec.mm) bits.push(spec.mm + ' mm');
        if (spec.spindle) bits.push(labelOf(spec.spindle));
        if (spec.speeds) bits.push(spec.speeds + 'v');
        if (spec.ring) bits.push(spec.ring.label);
        if (spec.bcd) bits.push('BCD ' + spec.bcd.replace('x', '×'));
        if (spec.shell) bits.push(labelOf(spec.shell));

        // The anchor line (the crank arms) carries the readable assembly so the
        // cart, the order confirmation and the packing list all show what was
        // configured. Every line carries the hidden id that ties them together.
        var assembly = sum.lines.map(function (l) { return trTitle(l.handle); }).join(' + ');
        var items = sum.lines.map(function (l, i) {
          var props = {};
          props[T.propId] = cid;
          if (i === 0) {
            props[T.propBuild] = assembly;
            props[T.propSpec] = bits.join(' · ');
          }
          return { id: l.id, quantity: 1, properties: props };
        });

        var url = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        }).then(function (r) {
          if (!r.ok) throw new Error('cart ' + r.status);
          return r.json();
        }).then(function () {
          msg.textContent = T.added;
          window.location.href = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
        }).catch(function () {
          msg.textContent = T.cartErr;
          btns.forEach(function (b) { b.disabled = false; });
        });
      }

      function copySummary() {
        var sum = R.summary(S);
        var txt = sum.lines.map(function (l) { return trTitle(l.handle) + (l.sub ? ' — ' + trSub(l.sub) : '') + ' · ' + eur(l.cents); }).join('\n');
        txt += '\n' + T.total + ': ' + eur(sum.cents) + ' · ' + (sum.weightComplete ? '≈ ' + grams(sum.grams) : T.weightUnavailable);
        if (navigator.clipboard) navigator.clipboard.writeText(txt);
        var b = $('[data-cb-copy]');
        if (b) { var old = b.textContent; b.textContent = T.copied; setTimeout(function () { b.textContent = old; }, 1600); }
      }

      /* ========================= events ========================= */
      root.addEventListener('click', function (e) {
        var t = e.target.closest('button');
        if (!t || !root.contains(t)) return;

        if (t.hasAttribute('data-cb-spindle')) { R.setSpindle(S, t.getAttribute('data-cb-spindle')); render(); return; }
        if (t.hasAttribute('data-cb-shell')) { R.setShell(S, t.getAttribute('data-cb-shell')); render(); return; }
        if (t.hasAttribute('data-cb-speeds')) { R.setSpeeds(S, t.getAttribute('data-cb-speeds')); render(); return; }
        if (t.hasAttribute('data-cb-opt')) {
          R.setOption(S, t.getAttribute('data-cb-opt'), +t.getAttribute('data-cb-idx'), t.getAttribute('data-cb-val'));
          render(); return;
        }
        if (t.hasAttribute('data-cb-next')) { step = nextStep(step); render(); focusStep(); return; }
        if (t.hasAttribute('data-cb-back')) { step = prevStep(step); render(); focusStep(); return; }
        if (t.hasAttribute('data-cb-goto')) { var g = +t.getAttribute('data-cb-goto'); if (stepEnabled(g) && !stepSkipped(g)) { step = g; render(); focusStep(); } return; }
        if (t.hasAttribute('data-cb-railbtn')) { var i = +t.getAttribute('data-cb-railbtn'); if (stepEnabled(i)) { step = i; render(); focusStep(); } return; }
        if (t.hasAttribute('data-cb-cart')) { addToCart(); return; }
        if (t.hasAttribute('data-cb-copy')) { copySummary(); return; }
        if (t.hasAttribute('data-cb-reset')) { S = R.freshState(); step = 0; render(); return; }
      });

      root.addEventListener('change', function (e) {
        var input = e.target;
        if (!input.matches || !input.matches('[data-cb-pick]')) return;
        var role = input.getAttribute('data-cb-pick');
        if (role === 'bolts' && input.value === '__own') {
          S.bolts = null; S.boltsOwn = true; R.revalidate(S); render(); return;
        }
        R.select(S, role, input.value || null);
        render();
      });

      function focusStep() {
        var h = $('.cb-step__title');
        if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: false }); }
      }

      /* ========================== boot ========================== */
      // rail labels come from i18n so the markup stays language-free
      var rail = $('[data-cb-rail]');
      rail.innerHTML = T.steps.map(function (label, i) {
        return '<button type="button" class="cb-rail__btn" data-cb-railbtn="' + i + '"><b>' + (i + 1) + '</b><span>' + esc(label) + '</span></button>';
      }).join('');
      $all('[data-cb-i18n]').forEach(function (el) {
        var k = el.getAttribute('data-cb-i18n');
        if (T[k]) el.textContent = T[k];
      });
      // aria-label text, same lookup table — the markup ships a Spanish
      // default so it still reads correctly before this script runs.
      $all('[data-cb-i18n-aria]').forEach(function (el) {
        var k = el.getAttribute('data-cb-i18n-aria');
        if (T[k]) el.setAttribute('aria-label', T[k]);
      });

      loadPrices();
      render();
    }
  }

  if (!customElements.get('crank-builder')) customElements.define('crank-builder', CrankBuilder);
})();
